import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingChat from "@/components/FloatingChat";
import ScheduleDialog from "@/components/ScheduleDialog";
import { ScheduleProvider, useSchedule } from "@/hooks/use-schedule";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Calendar, MessageCircle, CheckCircle, Clock, Heart, Shield, Sparkles } from "lucide-react";
import { Link } from "@tanstack/react-router";
import heroBackground from "@/assets/hero-background.jpg";
import claraPortrait from "@/assets/clara-portrait-final.png";

const oQueAcontece = [
  { step: "1", title: "Conversamos sobre você", desc: "Quero entender sua rotina, seus objetivos, sua relação com a comida e o que você já tentou antes. É uma conversa franca e sem julgamentos." },
  { step: "2", title: "Avalio sua situação", desc: "Com base no que você me contar, avalio o que faz sentido para o seu momento e onde posso ajudar." },
  { step: "3", title: "Explico como funciona o acompanhamento", desc: "Apresento como seria o processo, o que está incluído e quais são os valores, com total transparência." },
  { step: "4", title: "Você decide com calma", desc: "Se fizer sentido, combinamos o início do acompanhamento. Se não, você sai da conversa com mais clareza sobre o próximo passo." },
];

const diferenciais = [
  { icon: Clock, title: "Cerca de 30 minutos", desc: "Tempo suficiente para entender a sua situação e definir os próximos passos." },
  { icon: Heart, title: "Em português", desc: "Você explica sua rotina, seus hábitos e o que sente na sua língua, sem precisar traduzir nada." },
  { icon: Shield, title: "Espaço para falar abertamente", desc: "Você pode contar como realmente come, sem filtro. O objetivo é entender, não avaliar." },
  { icon: Sparkles, title: "Você sai com direcionamento", desc: "Mesmo que não siga com o acompanhamento, você sai da conversa sabendo o que priorizar." },
];

const CTAButtons = ({ openDialog }: { openDialog: () => void }) => (
  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
    <Button
      size="lg"
      variant="gradient"
      className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
      style={{ background: 'var(--gradient-green)' }}
      onClick={openDialog}
    >
      <Calendar className="mr-2 h-5 w-5" />
      Agendar consulta
    </Button>
    <Button
      size="lg"
      variant="gradient-outline"
      className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gradient-green)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
      onClick={() => window.open('https://wa.me/61423038977', '_blank')}
    >
      <MessageCircle className="mr-2 h-5 w-5" />
      Falar no WhatsApp
    </Button>
  </div>
);

export const faqItems = [
  {
    question: "Quanto tempo dura a consulta inicial?",
    answer: "Cerca de 30 minutos, por videochamada ou presencial no consultório em Hamilton (Brisbane). É uma conversa para eu entender sua rotina e seus objetivos e explicar como funciona o acompanhamento.",
  },
  {
    question: "Preciso me preparar ou levar exames?",
    answer: "Não é necessário. Se tiver exames recentes, pode trazer ou enviar, mas o objetivo da primeira conversa é conhecer você, não avaliar resultados.",
  },
  {
    question: "Vou sair da consulta com um plano alimentar?",
    answer: "A consulta inicial é uma avaliação. O plano alimentar personalizado faz parte do acompanhamento, e é nessa conversa que eu explico as opções, o investimento e o que você recebe.",
  },
  {
    question: "Como eu agendo?",
    answer: "Pelo botão de agendamento, que abre a minha agenda para você escolher o horário, ou pelo WhatsApp. Atendo em horários flexíveis, inclusive para quem trabalha em turnos.",
  },
  {
    question: "Tem reembolso do Medicare ou do plano de saúde?",
    answer: "Medicare não: o reembolso do Medicare é exclusivo de dietitians credenciadas (APD), e eu sou nutricionista. Plano de saúde privado depende do seu fundo e do pacote de extras; eu forneço o recibo e você confere a cobertura com o plano. Os valores já são pensados para funcionar sem reembolso.",
  },
  {
    question: "A consulta inicial tem custo?",
    answer: "Não. Ela não tem custo, não exige cartão e não cria obrigação de continuar. Se fizer sentido para você, combinamos os próximos passos.",
  },
];

const PageContent = () => {
  const { open, setOpen, openDialog } = useSchedule();

  return (
    <>

      <div className="min-h-screen">
        <Navigation />

        <main>
          {/* HERO */}
          <section className="relative overflow-hidden pt-24 sm:pt-32 pb-14 sm:pb-20">
            <div
              className="absolute inset-0 opacity-15"
              style={{
                backgroundImage: `url(${heroBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background to-transparent z-[2]" />

            <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-4xl text-center space-y-6 sm:space-y-8">
              <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                Clara Camille · Nutricionista
              </p>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-typewriter font-bold text-foreground leading-tight">
                Consulta inicial
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground font-sans leading-relaxed max-w-2xl mx-auto">
                Uma conversa de 30 minutos, sem custo, para eu conhecer sua rotina, entender o que você quer mudar e explicar <span className="italic gradient-text font-semibold">como posso ajudar</span>.
              </p>
              <CTAButtons openDialog={openDialog} />
            </div>
          </section>

          {/* O QUE ACONTECE */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Na prática
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  O que acontece na consulta inicial
                </h2>
              </div>

              <div className="space-y-6 sm:space-y-8">
                {oQueAcontece.map((item) => (
                  <div key={item.step} className="flex gap-4 sm:gap-6 items-start">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-typewriter font-bold text-lg sm:text-xl text-primary-foreground" style={{ background: 'var(--gradient-green)' }}>
                      {item.step}
                    </div>
                    <div className="space-y-1 sm:space-y-2 pt-1">
                      <h3 className="text-base sm:text-xl font-bold text-foreground font-sans">{item.title}</h3>
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* DIFERENCIAIS */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  O que esperar
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Como é a consulta
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-8">
                {diferenciais.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex gap-4 sm:gap-5 p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-primary/5 hover:bg-primary/10 transition-colors">
                      <div className="flex-shrink-0">
                        <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-primary/15 flex items-center justify-center">
                          <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-primary" strokeWidth={1.5} />
                        </div>
                      </div>
                      <div className="space-y-1 sm:space-y-2">
                        <h3 className="text-base sm:text-xl font-bold text-foreground font-sans">{item.title}</h3>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* QUEM TE ATENDE */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10 p-5 sm:p-8 rounded-2xl sm:rounded-3xl bg-primary/5">
                <div className="flex-shrink-0">
                  <img
                    src={claraPortrait}
                    alt="Clara Camille, nutricionista brasileira na Austrália"
                    className="w-28 h-28 sm:w-36 sm:h-36 rounded-full object-cover object-top shadow-lg"
                  />
                </div>
                <div className="text-center sm:text-left space-y-2 sm:space-y-3">
                  <h3 className="text-xl sm:text-2xl font-typewriter font-bold text-foreground">Clara Camille</h3>
                  <p className="text-sm sm:text-base text-primary font-typewriter font-semibold">Nutricionista · Brisbane, Austrália</p>
                  <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
                    Brasileira, nutricionista e imigrante. Atendo em português, presencialmente em Hamilton e online para toda a Austrália.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* COMO AGENDAR */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Agendamento
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Como agendar
                </h2>
              </div>

              <div className="max-w-2xl mx-auto space-y-6">
                <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-sm sm:text-base text-foreground font-sans leading-relaxed">
                    <strong>Pela agenda online:</strong> clique no botão abaixo e escolha o horário que preferir.
                  </p>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-sm sm:text-base text-foreground font-sans leading-relaxed">
                    <strong>Pelo WhatsApp:</strong> envie uma mensagem e combinamos o melhor horário.
                  </p>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-sm sm:text-base text-foreground font-sans leading-relaxed">
                    <strong>Online ou presencial:</strong> a consulta pode ser por videochamada (de qualquer cidade) ou presencial em Brisbane.
                  </p>
                </div>
              </div>

              <div className="mt-10 sm:mt-14">
                <CTAButtons openDialog={openDialog} />
              </div>

              {/* Internal link */}
              <div className="mt-10 text-center">
                <p className="text-sm text-muted-foreground font-sans">
                  Quer saber mais antes de agendar? Visite a página{" "}
                  <Link to="/nutricionista-brasileira-na-australia" className="gradient-text italic font-semibold hover:opacity-80 transition-opacity">
                    Nutricionista brasileira na Austrália
                  </Link>{" "}
                  ou conheça a{" "}
                  <Link to="/consulta-nutricional-online-australia" className="gradient-text italic font-semibold hover:opacity-80 transition-opacity">
                    consulta online
                  </Link>.
                </p>
              </div>
            </div>
          </section>

          {/* FINAL CTA */}
          {/* FAQ */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
              <div className="text-center mb-8 sm:mb-12 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">Dúvidas comuns</p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">Perguntas frequentes sobre a consulta inicial</h2>
              </div>
              <Accordion type="single" collapsible className="space-y-3">
                {faqItems.map((item, index) => (
                  <AccordionItem key={index} value={`faq-${index}`} className="border border-border rounded-xl px-4 sm:px-6 bg-background">
                    <AccordionTrigger className="text-left text-sm sm:text-base font-sans font-medium text-foreground hover:no-underline py-4 sm:py-5">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed pb-4 sm:pb-5">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          <section className="py-16 sm:py-24 lg:py-32 relative overflow-hidden">
            <div
              className="absolute inset-0 blur-sm scale-105"
              style={{
                backgroundImage: `url(${heroBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div className="absolute inset-0 opacity-80" style={{ background: 'var(--gradient-green)' }} />

            <div className="container mx-auto px-4 sm:px-6 relative z-10 flex justify-center">
              <div
                className="max-w-2xl w-full text-center space-y-6 sm:space-y-8 rounded-2xl sm:rounded-3xl px-5 py-10 sm:px-8 sm:py-12 lg:px-14 lg:py-16"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(24px) saturate(1.4)',
                  WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
              >
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-typewriter font-bold text-white leading-tight">
                  Vamos conversar?
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-white/90 font-sans leading-relaxed">
                  Trinta minutos para entender o seu momento e definir os próximos passos da sua alimentação.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2">
                  <Button
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 bg-white text-foreground hover:bg-white/90 font-semibold shadow-lg w-full sm:w-auto"
                    onClick={openDialog}
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Agendar consulta
                  </Button>
                  <Button
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 font-semibold shadow-lg border-0 w-full sm:w-auto"
                    style={{ background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(12px)', color: 'white' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
                    onClick={() => window.open('https://wa.me/61423038977', '_blank')}
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Falar no WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <FloatingChat />
        <ScheduleDialog open={open} onOpenChange={setOpen} />
      </div>
    </>
  );
};

const ConsultaInicialGratuita = () => (
  <ScheduleProvider>
    <PageContent />
  </ScheduleProvider>
);

export default ConsultaInicialGratuita;
